import React, { createContext, useContext, useLayoutEffect, useRef, useState } from "react";
import "./PrintField.css";

// FormWrapper provides the capture / print mode here, so forms can use PrintField directly as a stable component.
// (Forms used to build `InputField` inside render: a new component type on every render, which remounted every
// field and wiped what the customer typed — the submitted image and the print came out blank.)
export const PrintModeContext = createContext({ isCapturing: false, isPrintMode: false });

// How a printed field (the span that replaces an input on the captured / printed paper) is sized:
// - text that fits keeps the field's exact width (the line does not reflow); text up to FIT_SLACK px wider takes its
//   own width (it used to keep the field width and run over the next word);
// - a field that ends its line (nothing after it but a line break) may overflow up to END_SLACK px into the paper's
//   side margin — e.g. "2569" in the 40px year box at the end of agenda 1 used to drop to its own line;
// - anything longer (an address) flows inline with the sentence and wraps like normal text — as an inline-block it
//   jumped to its own line and left a justified gap behind ("ณ            สำนักงานเลขที่").
export const FIT_SLACK = 24;
const END_SLACK = 56;

function endsLine(el) {
  for (let n = el.nextSibling; n; n = n.nextSibling) {
    if (n.nodeName === "BR") return true;
    if (n.nodeType === 3 ? n.textContent.trim() !== "" : true) return false;
  }
  return true;
}

// el: the printed span laid out with width:auto; fieldWidth: the input's on-screen width
export function fitPrintedField(el, fieldWidth) {
  if (!el || !fieldWidth) return;
  const over = el.offsetWidth - fieldWidth;
  if (over <= 0 || (over <= END_SLACK && endsLine(el))) {
    el.style.width = `${fieldWidth}px`;
    el.style.whiteSpace = "pre";
  } else if (over <= FIT_SLACK) {
    // a little longer than the box: the box takes the text's own width — keeping the box width let the text run
    // over the next word ("ข้อความ 9ปี", 28-10-2569)
    el.style.whiteSpace = "pre";
  } else {
    el.style.display = "inline";
    el.style.whiteSpace = "normal";
    // keep a little space from the words around it (the box used to give that space)
    el.style.margin = "0 4px";
  }
}

function PrintField({
  value,
  defaultValue,
  onChange,
  className,
  style,
  placeholder,
  isPrintMode,
  isCapturing,
  // printFit: in capture / print mode take only the width of the text (no reserved blank space)
  printFit = false,
  // optional: may stay empty when the paper is sent (printFit fields — e.g. address line 2 — are optional unless
  // optional={false}, like the director's name inside "( … )")
  optional,
  name,
  type = "text",
  ...rest
}) {
  const mode = useContext(PrintModeContext);
  // uncontrolled fields keep what was typed here, so capture / print mode can show it
  const [typed, setTyped] = useState(defaultValue ?? "");
  // on-screen width of the input, so the printed text keeps at least the same space but can grow for longer text
  const inputRef = useRef(null);
  const spanRef = useRef(null);
  const widthRef = useRef(0);
  const controlled = value !== undefined;
  const fieldValue = controlled ? value : typed;
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (input) {
      // the field's own width (from its CSS) is what the printed text is fitted against
      input.style.width = style?.width ?? "";
      widthRef.current = input.offsetWidth;
      // owner 28-10-2569: long text was cut off inside the fixed-width box while typing — the box now grows with
      // the text (never wider than its line); anything longer still scrolls and shows in full as a tooltip
      if (input.scrollWidth > input.clientWidth + 1) {
        input.style.width = `${Math.ceil(input.scrollWidth + (input.offsetWidth - input.clientWidth) + 6)}px`;
      }
    }
    if (spanRef.current && !printFit) fitPrintedField(spanRef.current, widthRef.current);
  });
  const isEmpty = fieldValue === undefined || fieldValue === null || String(fieldValue).trim() === "";
  const displayValue = isEmpty ? " " : String(fieldValue);

  if ((isPrintMode ?? mode.isPrintMode) || (isCapturing ?? mode.isCapturing)) {
    return (
      <span
        ref={spanRef}
        className={`mfw-print-field ${className || ""}${isEmpty ? " mfw-print-empty" : ""}`}
        style={{
          ...style,
          width: "auto",
          minWidth: printFit ? 0 : widthRef.current ? `${widthRef.current}px` : style?.width,
          maxWidth: "100%",
          whiteSpace: "pre-wrap",
        }}
      >
        {displayValue}
      </span>
    );
  }

  return (
    <input
      {...rest}
      ref={inputRef}
      className={className}
      style={{ ...style, maxWidth: "100%" }}
      placeholder={placeholder}
      name={name}
      type={type}
      title={fieldValue ? String(fieldValue) : undefined}
      data-optional={(optional ?? printFit) ? "1" : undefined}
      value={fieldValue ?? ""}
      onChange={(event) => {
        if (!controlled) setTyped(event.target.value);
        if (onChange) onChange(event);
      }}
    />
  );
}

export default PrintField;
